import { useRef, useEffect } from 'preact/hooks';
import { Header } from './Header';
import { MessagesArea } from './MessagesArea';
import { InputArea } from './InputArea';
import { Footer } from './Footer';
import type { Message } from '@/types';
import s from './ChatWindow.module.css';

interface ChatWindowProps {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  onSendMessage: (content: string) => void;
  onRate: (messageId: string, rating: 'positive' | 'negative', feedback?: string) => void;
  onEmailSubmit: (questionId: string, email: string) => Promise<void>;
  onClearError: () => void;
  onClose: () => void;
  isRateLimited: boolean;
  /** Questions shown on empty chat */
  suggestedQuestions?: string[];
  /** Questions shown after last bot response */
  followUpQuestions?: string[] | null;
  /** Whether user has sent at least one message */
  hasSentMessage?: boolean;
  /** Custom header text from settings */
  headerText?: string;
  /** Custom input placeholder from settings */
  inputPlaceholder?: string;
  /** Embedded mode - fills container, no close button, no Escape key */
  embedded?: boolean;
  /** Current input text value (controlled) */
  inputValue: string;
  /** Called when input text changes */
  onInputChange: (value: string) => void;
}

export function ChatWindow({
  messages,
  isLoading,
  error,
  onSendMessage,
  onRate,
  onEmailSubmit,
  onClearError,
  onClose,
  isRateLimited,
  suggestedQuestions,
  followUpQuestions,
  hasSentMessage,
  headerText,
  inputPlaceholder,
  embedded = false,
  inputValue,
  onInputChange,
}: ChatWindowProps) {
  const windowRef = useRef<HTMLDivElement>(null);

  // Escape key closes the chat (disabled in embedded mode)
  useEffect(() => {
    if (embedded) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, embedded]);

  // Focus trap: auto-focus the textarea on mount
  useEffect(() => {
    const textarea = windowRef.current?.querySelector('textarea');
    textarea?.focus();
  }, []);

  return (
    <div
      ref={windowRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="widget-chat-title"
      class={`${s.window}${embedded ? ` ${s.embedded}` : ''}`}
    >
      <Header onClose={onClose} headerText={headerText} embedded={embedded} />
      <MessagesArea
        messages={messages}
        isLoading={isLoading}
        onRate={onRate}
        onEmailSubmit={onEmailSubmit}
        suggestedQuestions={suggestedQuestions}
        followUpQuestions={followUpQuestions}
        hasSentMessage={hasSentMessage}
        onQuestionClick={onSendMessage}
      />
      <InputArea
        value={inputValue}
        onChange={onInputChange}
        onSend={onSendMessage}
        isLoading={isLoading}
        isDisabled={isRateLimited}
        error={error}
        onClearError={onClearError}
        placeholder={inputPlaceholder}
      />
      <Footer />
    </div>
  );
}
